/**
 * File rules for the 835 Import & Conversion upload.
 *
 * ACCEPTED_835_FILE_EXTENSIONS and the size/count limits below are the single source of truth:
 * they feed both the Kendo Upload `restrictions` prop and the text shown to the user, so the
 * enforced rules and the displayed rules cannot drift apart.
 *
 * Why 25 MB per file:
 * The 28.6 MB limit in Microsoft Internet Information Services (IIS) is the default maximum
 * file upload size restriction. In IIS, the configuration property maxAllowedContentLength is
 * set by default to 30,000,000 bytes (which equates to exactly 28.61 MB). When an upload
 * exceeds this "cliff," the server instantly rejects the request and throws a
 * HTTP Error 404.13 - CONTENT_LENGTH_TOO_LARGE error.
 *
 * A 25 MB per-file cap sits safely below that cliff, so no host limit has to be raised and an
 * oversized file is reported in the browser instead of failing mid-request with a bare 404.13.
 *
 * These limits are a user-experience affordance only. The upload endpoint must re-validate
 * extension and size server-side before trusting any file.
 */

export const ACCEPTED_835_FILE_EXTENSIONS = [
  ".835",
  ".dat",
  ".txt",
  ".x12",
  ".edi",
  ".rmt",
  ".zip",
  ".tar",
  ".pgp",
  ".gpg",
  ".asc",
];

export const ACCEPTED_835_FILE_EXTENSIONS_DISPLAY =
  ACCEPTED_835_FILE_EXTENSIONS.join(", ");

export const REJECTED_835_FILE_TYPES_MESSAGE =
  "PDF, Excel, Word, and CSV files are not accepted.";

export const MAX_835_FILE_SIZE_BYTES = 25 * 1024 * 1024;

export const MAX_835_FILE_SIZE_DISPLAY = "25 MB";

/** Guards against empty files: a 0-byte remittance file is never valid. */
export const MIN_835_FILE_SIZE_BYTES = 1;

export const MAX_835_FILE_COUNT = 5;

export const FILE_835_LIMITS_MESSAGE = `Limits: up to ${MAX_835_FILE_COUNT} files per submission, ${MAX_835_FILE_SIZE_DISPLAY} maximum per file. Empty files are not accepted.`;

export const TOO_MANY_835_FILES_MESSAGE = `You can submit a maximum of ${MAX_835_FILE_COUNT} files at a time. Please remove files before submitting.`;

export const UPLOAD_835_HEADING = "Upload 835 Files";

/**
 * Walks the user through the upload sequence.
 *
 * The limits are interpolated from the constants that feed the Kendo Upload `restrictions`
 * prop rather than being restated, so the explanation cannot drift from the rules actually
 * enforced.
 */
export const UPLOAD_835_HELP_TEXT = `Drag files onto the drop zone below, or use Select files to browse your computer. You can submit up to ${MAX_835_FILE_COUNT} files at a time, ${MAX_835_FILE_SIZE_DISPLAY} maximum per file. Review the selected files, then choose Submit Files. Each upload is recorded against the organization displayed in the header.`;

export const UPLOAD_835_HELP_LABEL = "How to upload 835 files";

export const REMOVE_ALL_835_FILES_LABEL = "Remove All";

/**
 * Reasons shown for files the Kendo Upload rejected.
 *
 * Kendo's own per-row messages are deliberately generic ("File size too large."), so these
 * restate the rule that was actually broken. They are built from the same constants that feed
 * the Upload `restrictions` prop so the wording cannot drift from what is enforced.
 */
export const FILE_835_VALIDATION_REASONS = {
  invalidFileExtension: `is not an accepted format. Accepted formats: ${ACCEPTED_835_FILE_EXTENSIONS_DISPLAY}.`,
  invalidMaxFileSize: `exceeds the ${MAX_835_FILE_SIZE_DISPLAY} maximum file size.`,
  invalidMinFileSize: "is empty. Empty files cannot be submitted.",
};

export const REJECTED_835_FILES_HEADING =
  "These files cannot be submitted. Remove them to continue:";

/**
 * Upload endpoint for the selected 835 files.
 *
 * Resolves to ImportConversion835Controller.SaveFile, which inherits the
 * "api/[controller]/[action]" route from RootController. The Kendo Upload posts to this URL
 * once per file (`batch` is false) using its built-in transport, matching the approach used
 * for Missing Remittance attachments.
 */
export const SAVE_835_FILES_URL = "api/ImportConversion835/SaveFile";

export const SUBMIT_835_FILES_LABEL = "Submit Files";

export const SUBMITTING_835_FILES_LABEL = "Submitting...";

export const SUBMITTED_835_FILES_MESSAGE =
  "All files were submitted successfully.";

export const FAILED_835_FILES_HEADING =
  "These files could not be submitted. Use the retry button next to a file to send it again, or remove it:";

export const FAILED_835_FILE_MESSAGE_SUFFIX = "failed to upload.";

/**
 * Shown when no organization is selected in the application header.
 *
 * Every upload is recorded against the selected organization, so submitting without one would
 * produce an unattributable record and is blocked on both the client and the server.
 */
export const NO_835_ORGANIZATION_MESSAGE =
  "Select an organization in the header before submitting files.";

/**
 * Upload history endpoint for the 835 Import & Conversion page.
 *
 * Resolves to ImportConversion835Controller.GetUploadHistory. The organization is passed as
 * query string parameters because this is a GET with no body.
 */
export const UPLOAD_HISTORY_835_URL = "api/ImportConversion835/GetUploadHistory";

/**
 * How far back the history shown on the page reaches.
 *
 * Display only. The window is enforced server-side by UploadHistoryDaysToDisplay on
 * ImportConversion835Controller, so this constant must be kept in step with it - it does not
 * control the query.
 */
export const UPLOAD_HISTORY_835_DAYS = 30;

export const UPLOAD_HISTORY_835_HEADING = `Uploads in the last ${UPLOAD_HISTORY_835_DAYS} days`;

/**
 * Sets expectations about what a history row does and does not mean.
 *
 * A row is written when a file is received. It does not indicate that the file has been
 * converted or processed downstream, so the page should not imply that it has.
 */
export const UPLOAD_HISTORY_835_NOTE =
  "Shows files received for the organization selected in the header.";

/**
 * The longer explanation behind the history heading's info icon.
 *
 * Says plainly what a row does not mean. The file content currently stops at the storage seam
 * in SaveFile, so a reader seeing a row could easily assume the file has already been
 * converted - this is the one place that corrects that assumption.
 */
export const UPLOAD_HISTORY_835_HELP_TEXT = `Lists the files received for the organization selected in the header over the last ${UPLOAD_HISTORY_835_DAYS} days, newest first. The list refreshes after you submit files, and changes when you switch organizations. A row means the file was received - it does not mean the file has been converted or processed.`;

export const UPLOAD_HISTORY_835_HELP_LABEL = "About the upload history";

export const UPLOAD_HISTORY_835_LOADING_MESSAGE = "Loading upload history...";

export const UPLOAD_HISTORY_835_ERROR_MESSAGE =
  "The upload history could not be loaded.";

export const UPLOAD_HISTORY_835_EMPTY_MESSAGE = `No files have been uploaded for this organization in the last ${UPLOAD_HISTORY_835_DAYS} days.`;

export const UPLOAD_HISTORY_835_NO_ORGANIZATION_MESSAGE =
  "Select an organization in the header to see its upload history.";

/**
 * How each stored UploadRequestStatus is presented as a result card.
 *
 * The keys must stay in step with ImportConversion835UploadStatus on the server and with the
 * CHECK constraint on ImportConversion835UploadHistory. A value outside this map is handled by
 * getUploadResultPresentation rather than being allowed to fail a property lookup.
 *
 * `defaultMessage` is what the card shows when the row carries no ErrorMessage. The Success copy
 * deliberately says only that the file was received: nothing validates or processes the content
 * yet, so promising more would be inaccurate.
 */
export const UPLOAD_RESULT_835_STATUS = {
  Success: {
    title: "Validation Successful",
    defaultMessage: "File received and recorded.",
    statusClass: "success",
    icon: "\u2713",
  },
  Failed: {
    title: "Validation Failed",
    defaultMessage: "The file could not be processed. No reason was recorded.",
    statusClass: "error",
    icon: "\u2715",
  },
  Uploaded: {
    title: "File Uploaded",
    defaultMessage: "File is uploaded and awaiting processing.",
    statusClass: "pending",
    icon: "\u2022",
  },
};

/**
 * Fallback for a status the map above does not recognise.
 *
 * A row written with an unexpected status must still render as a readable, neutral card rather
 * than bringing the page down on an undefined lookup.
 */
export const UPLOAD_RESULT_835_UNKNOWN_STATUS = {
  title: "Upload Recorded",
  defaultMessage: "The status of this upload is not recognised.",
  statusClass: "pending",
  icon: "\u2022",
};

export const FAQ_835_HEADING = "Frequently Asked Questions";

/**
 * FAQ content for the 835 Import & Conversion page.
 *
 * This is business-authored copy provided directly by Net-Rx Customer Service, not a
 * restatement of the file-rule constants above - for example the accepted-formats list in the
 * "file types" answer is deliberately the plain-language subset the business wants shown here,
 * not the full ACCEPTED_835_FILE_EXTENSIONS list enforced by the Upload restrictions.
 */
export const FAQ_835_ITEMS = [
  {
    id: "processing-time",
    question: "How long does it take to process my file?",
    answer:
      "Most files finish processing within 24 hours. In some cases it can take up to 48 hours. If your file hasn't completed after 48 hours, please reach out to us so we can look into it.",
  },
  {
    id: "file-error-status",
    question: "My file shows an error. What do I do?",
    answer:
      'If a file can\u2019t be processed, it will show an "Error" status in your uploaded files list, along with a short note explaining why (for example, the file wasn\u2019t a valid 835, it was out of balance, or your pharmacy ID wasn\u2019t recognized). Read the note, fix the issue, and upload the file again. If you need help, use the "next steps" link shown with the error, or contact us.',
  },
  {
    id: "accepted-file-types",
    question: "What kind of files can I upload?",
    answer:
      "You can upload your 835 remittance files directly. Accepted file types are: .835, .dat, .txt, and compressed .zip or .tar files that contain those. If you upload a .zip or .tar, we'll open it for you automatically. Other file types (such as PDF, Excel, or Word documents) can't be uploaded here.",
  },
  {
    id: "verify-835-format",
    question: "How can I tell if my file is really an 835 file before uploading?",
    answer:
      'A valid 835 remittance file is a plain-text file that begins with the letters "ISA" at the very start. You can check by opening the file in a basic text editor (like Notepad): if the first three characters are "ISA", it\u2019s in the right format. If you instead see rows of a spreadsheet, scrambled symbols, or unreadable text, it is not an 835 file and won\u2019t be accepted.',
  },
  {
    id: "duplicate-file-upload",
    question: "What happens if I upload the same file twice?",
    answer:
      "If a file is already loaded to your account and you upload it again, our system recognizes the duplicate and ignores it, so the same payment isn't processed twice. If you're re-uploading to replace or fix a version of a file that's already posted to your account, please contact the Net-Rx support team so they can delete the file you need to replace first.",
  },
  {
    id: "contact-support",
    question: "Who do I contact if I need help?",
    answer:
      "Reach out to the Net-Rx Customer Service team:\nPhone: 866-336-3879\nEmail: customerservice@net-rx.com",
  },
];
